import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import headerBackgroundDefaultCoords from 'config/sectionHeader/headerBackgroundDefaultCoords';
import { sectionHeaderPropTypes } from 'config/sectionHeader/sectionHeaderPropTypes';
import headerBackgroundMoveDirections from 'config/sectionHeader/headerBackgroundMoveDirections';
import headerBackgroundImageSize from 'config/sectionHeader/headerBackgroundImageSize';
import headerContentAlignment from 'config/sectionHeader/headerContentAlignment';
import getImageProps from 'utils/getImageProps';
import { calculateMoveDirection } from 'utils/headerBackgroundUtils';
import { makePromiseCancelable } from '@nextiva/engagement-ui';

const withHeaderBackgroundProps = WrappedComponent => {
  class WithLogicComponent extends Component {
    static propTypes = {
      ...sectionHeaderPropTypes,
      isFullWidth: PropTypes.bool,
      isHeaderFullWidth: PropTypes.bool,
    };

    static defaultProps = {
      backgroundImageSize: headerBackgroundImageSize.cover,
      logoImageAlign: headerContentAlignment.left,
      isFullWidth: false,
      // `isHeaderFullWidth` is null by default to recalculate styles when it takes boolean from redux store @sinslav
      isHeaderFullWidth: null,
      backgroundCoords: headerBackgroundDefaultCoords,
      isEditMode: false,
    };

    state = {
      imageMoveDirection: headerBackgroundMoveDirections.any,
      imgSize: {
        width: 0,
        height: 0,
      },
      containerWidth: 0,
      opacity: 0.01,
    };

    componentDidMount() {
      // Calculate box-size later, because right on didmount box can be affected
      // by other elements @etiurin
      this.timer = setTimeout(() => {
        this.calculateImageContainerWidth();
        this.calculateImageRealSizeAndMoveDirection();
        // Show component after it's background size and position are drawn
        // (this fixes blinking effect) @etiurin
        this.timerOpacity = setTimeout(() => {
          this.setState({ opacity: 1 });
        });
      });
      window.addEventListener('resize', this.handlePageResize);
    }

    componentWillUnmount() {
      clearTimeout(this.timer);
      clearTimeout(this.timerOpacity);
      window.removeEventListener('resize', this.handlePageResize);
      if (this.getImagePropsPromise) {
        this.getImagePropsPromise.cancel();
      }
    }

    componentWillReceiveProps(nextProps) {
      const hasImageChanged =
        nextProps.backgroundImageUrl !== this.props.backgroundImageUrl;
      const hasContainerHeightChanged = nextProps.height !== this.props.height;
      const hasBackgroundImageSizeChanged =
        nextProps.backgroundImageSize !== this.props.backgroundImageSize;

      if (hasImageChanged) {
        this.calculateImageRealSizeAndMoveDirection(nextProps);
      }

      if (hasContainerHeightChanged || hasBackgroundImageSizeChanged) {
        this.calculateMoveDirection();
      }
    }

    componentDidUpdate(prevProps) {
      const hasHeaderFullWidthChanged =
        this.props.isHeaderFullWidth !== prevProps.isHeaderFullWidth;
      const hasFullWidthChanged =
        prevProps.isFullWidth !== this.props.isFullWidth;

      if (hasFullWidthChanged || hasHeaderFullWidthChanged) {
        this.calculateImageContainerWidth();
        this.calculateMoveDirection();
      }
    }

    calculateImageRealSizeAndMoveDirection = async function(props) {
      const { backgroundImageUrl } = props || this.props;
      this.getImagePropsPromise = makePromiseCancelable(
        getImageProps(backgroundImageUrl),
      );

      const { width, height } = await this.getImagePropsPromise.promise;
      this.setState(
        { imgSize: { width, height } },
        this.calculateMoveDirection,
      );
    };

    getContainerSize = (state = this.state, props = this.props) => ({
      width: state.containerWidth,
      height: props.height,
    });

    handlePageResize = debounce(
      () => {
        this.calculateImageContainerWidth();
        this.calculateMoveDirection();
      },
      { maxWait: 150 },
    );

    calculateImageContainerWidth = () => {
      if (this.container) {
        this.setState({
          containerWidth: this.container.offsetWidth,
        });
      }
    };

    calculateMoveDirection = () => {
      this.setState((state, props) => {
        const { backgroundImageSize } = props;
        const { imgSize } = state;

        return {
          imageMoveDirection: calculateMoveDirection(
            backgroundImageSize,
            this.getContainerSize(state, props),
            imgSize,
          ),
        };
      });
    };

    render = () => {
      const { imageMoveDirection, imgSize, opacity } = this.state;

      return (
        <div
          ref={el => {
            this.container = el;
          }}
          style={{ opacity }}
        >
          <WrappedComponent
            imageMoveDirection={imageMoveDirection}
            imgSize={imgSize}
            containerSize={this.getContainerSize()}
            {...this.props}
          />
        </div>
      );
    };
  }

  return WithLogicComponent;
};

export default withHeaderBackgroundProps;
